import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { myProgress, myPeerReviewStats, myPeerReviewModuleStatus } from "@/lib/apiClient";
import { checkAchievements } from "@/lib/achievements";
import { resolveModuleName, resolveModuleIntro, resolveProgramName, resolveModuleSubtitle } from "@/lib/i18nContent";
import InstallBanner from "@/components/InstallApp";
import CommunityLiveLearning from "@/components/CommunityLiveLearning";
import {
  ArrowRight, CheckCircle2, Circle, Play, ListChecks, Award,
  Lock, Mic, BookOpen, Users, ShieldCheck, Trophy, Bell,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [peer, setPeer] = useState(null);
  const [peerModuleStatus, setPeerModuleStatus] = useState(null);
  const [achievements, setAchievements] = useState([]);

  useEffect(() => {
    myProgress().then(setData);
    myPeerReviewStats().then(setPeer).catch(() => {});
    myPeerReviewModuleStatus().then(setPeerModuleStatus).catch(() => {});
    checkAchievements().then(setAchievements);
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col"><Header />
        <div className="flex-1 max-w-[1280px] mx-auto w-full px-5 sm:px-8 py-10">
          <div className="h-10 w-64 bg-[#E7E0D2] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const p = data.progress || {};
  const mods = p.modules || {};
  const modules = data.modules || [];
  const completedCount = modules.filter((m) => mods[m.code]?.passed).length;
  const progressPct = Math.round(100 * completedCount / modules.length);
  const nextModule = modules.find((m) => !mods[m.code]?.passed) || modules[0];
  const currentStage = nextModule ? t("dashboard.module_n", { n: nextModule.n }) : t("dashboard.all_modules_complete");

  return (
    <div className="min-h-screen flex flex-col" data-testid="dashboard-page">
      <Header />

      {/* Install strip — same component and same dismissal as the landing page. */}
      <InstallBanner alwaysShow />

      <main className="flex-1 max-w-[1280px] mx-auto w-full px-5 sm:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{greeting(t).replace(/[,،]\s*$/, "")}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">
              {t("dashboard.salam_greeting", { name: user?.name?.split(" ")[0] || "" })}
            </h1>
            <p className="text-[#4B5563] mt-2">{t("dashboard.current_stage")} <span className="font-medium text-[#1F2937]">{currentStage}</span></p>
          </div>
          <Link data-testid="cta-modules" to="/modules"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium transition">
            <ListChecks className="w-4 h-4" /> {t("dashboard.open_all_modules")}
          </Link>
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-3xl bg-[#064E3B] text-[#FDFBF7] p-8 relative overflow-hidden">
            <div className="absolute inset-0 pattern-islamic opacity-25" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.22em] text-[#FED7AA]">{t("dashboard.up_next")}</div>
              <div className="font-heading mt-3 text-2xl sm:text-3xl font-medium">
                {t("dashboard.module_n", { n: nextModule?.n })} · {nextModule && resolveModuleName(t, nextModule)}
              </div>
              <p className="mt-3 text-[#FDFBF7]/75 max-w-md">{nextModule && resolveModuleIntro(t, nextModule)}</p>
              <Link data-testid="continue-module-btn" to={`/modules/${nextModule?.code}`}
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-medium transition">
                <Play className="w-4 h-4" /> {t("dashboard.continue_module")}
              </Link>
            </div>
          </div>

          <div className="rounded-3xl bg-white border border-[#E7E0D2] p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563]">{t("dashboard.learning_journey")}</div>
            <ul className="mt-4 space-y-3" data-testid="learning-journey">
              {data.learning_journey.map((item) => {
                const isFluencyInApp = item.code === "quran-fluency";
                const isCompleted = isFluencyInApp && progressPct === 100;
                const isActive = isFluencyInApp && progressPct < 100;
                return (
                  <li key={item.code} className="flex items-center gap-3 text-sm" data-testid={`journey-${item.code}`}>
                    {isCompleted ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                      : isActive ? <span className="w-4 h-4 rounded-full bg-[#D97706] grid place-items-center text-white text-[8px]" style={lang === "ur" ? { transform: "scaleX(-1)" } : undefined}>▶</span>
                      : <Circle className="w-4 h-4 text-[#4B5563]/50" />}
                    <Link to={isFluencyInApp ? "/modules" : `/programs/${item.program_id}`}
                      className={`flex-1 ${isActive ? "font-semibold text-[#1F2937]" : isCompleted ? "text-[#10B981]" : "text-[#4B5563]"} hover:text-[#064E3B]`}>
                      {resolveProgramName(t, item)}
                      {isActive && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#D97706]">{t("dashboard.in_progress")}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Community & Live Learning — WhatsApp channel (fixed public link)
            + Live Zoom Class (admin-configured via app_config, same source
            data.config already reads beta_mode from). Placed ahead of the
            three-layer cards so the community/live-events prompt is the
            first thing a returning learner sees below their journey. */}
        <CommunityLiveLearning t={t} lang={lang} community={data?.community} />

        {/* SRS v2.0 Section 10 — Three-layer assessment cards. Each layer
            is independent; peer review never blocks certification. */}
        <ThreeLayerCards
          t={t}
          modulesPassed={completedCount}
          modulesTotal={modules.length}
          peerModulesComplete={(peerModuleStatus?.modules || []).filter((m) => m.complete).length}
          peerModulesTotal={peerModuleStatus?.modules?.length ?? modules.length}
          certificateStatus={data?.score_breakdown?.final_score > 0 ? "in_progress" : "not_submitted"}
          finalScore={data?.score_breakdown?.final_score ?? 0}
        />

        <PendingReviewsAlert peer={peer} />

        {!["admin", "support", "mentor"].includes(user?.role) && (
          <AchievementsTile achievements={achievements} t={t} />
        )}

        <div className="mt-12">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937]">{t("dashboard.your_modules")}</h2>
            <Link to="/modules" className="text-sm text-[#064E3B] font-medium hover:underline">{t("dashboard.view_all")}</Link>
          </div>
          <ModulesPanel modules={modules} mods={mods} t={t} betaMode={Boolean(data.config?.beta_mode)} />
        </div>
      </main>
      <Footer />
    </div>
  );
}

/**
 * Modules panel — adds Learn / Practice tabs and the sequential unlock
 * UX. A module is `locked` until the module immediately before it has
 * been passed. Locked cards display the Lock icon, are non-clickable,
 * and surface the unlock hint.
 *
 * Learn = view content / Listen-all.   Practice = drill + final test.
 */
function ModulesPanel({ modules, mods, t, betaMode = false }) {
  const [tab, setTab] = useState("learn"); // "learn" | "practice"

  // Sequential unlock: module N is unlocked iff N === 1 OR the previous
  // module is `passed`. SRS v2.0 Section 7 — when `betaMode` is ON, every
  // module is unlocked regardless of previous results so testers can hop
  // freely. (Independent of `tab` — a user can still Learn ahead but
  // cannot Practice ahead in production mode.)
  const isUnlocked = (idx) => {
    if (betaMode) return true;
    if (idx === 0) return true;
    const prev = modules[idx - 1];
    return Boolean(mods[prev?.code]?.passed);
  };

  return (
    <div data-testid="modules-panel" className="mt-5">
      {/* Tabs */}
      {/* <div className="inline-flex p-1 rounded-full bg-[#F5EFE3] border border-[#E7E0D2] mb-5" data-testid="learn-practice-tabs">
        <button
          data-testid="tab-learn"
          onClick={() => setTab("learn")}
          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition ${
            tab === "learn" ? "bg-[#064E3B] text-white shadow-sm" : "text-[#4B5563] hover:text-[#1F2937]"
          }`}>
          <BookOpen className="w-3.5 h-3.5" /> {t("dashboard.tabs.learn")}
        </button>
        <button
          data-testid="tab-practice"
          onClick={() => setTab("practice")}
          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition ${
            tab === "practice" ? "bg-[#064E3B] text-white shadow-sm" : "text-[#4B5563] hover:text-[#1F2937]"
          }`}>
          <Mic className="w-3.5 h-3.5" /> {t("dashboard.tabs.practice")}
        </button>
      </div> */}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((m, idx) => {
          const mp = mods[m.code] || {};
          // Sequential lock applies to BOTH tabs — module N+1 is locked
          // until module N is passed. (Module 1 is always unlocked.)
          const locked = !isUnlocked(idx);
          const url = tab === "practice"
            ? `/modules/${m.code}?mode=practice`
            : `/modules/${m.code}?mode=learn`;

          if (locked) {
            return (
              <div
                key={m.code}
                data-testid={`module-card-${m.code}`}
                data-locked="1"
                aria-disabled="true"
                className="group rounded-2xl bg-[#F5EFE3]/50 border-2 border-dashed border-[#E7E0D2] p-5 relative cursor-not-allowed select-none">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl grid place-items-center font-mono text-sm font-bold text-[#9CA3AF] bg-[#FDFBF7] border border-[#E7E0D2]">
                    {String(m.n).padStart(2, "0")}
                  </div>
                  <span
                    data-testid={`lock-icon-${m.code}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#9CA3AF] bg-white border border-[#E7E0D2] rounded-full px-2 py-0.5">
                    <Lock className="w-3 h-3" /> {t("modules.locked")}
                  </span>
                </div>
                <div className="font-heading mt-4 text-base font-medium text-[#9CA3AF]">{resolveModuleName(t, m)}</div>
                <div className={resolveModuleSubtitle(t, m) === m.name_ar ? "font-arabic text-sm text-[#9CA3AF]" : "text-sm text-[#9CA3AF]"}>{resolveModuleSubtitle(t, m)}</div>
                <div className="mt-3 text-xs text-[#9CA3AF]">
                  {t("modules.locked_hint", { n: m.n - 1 })}
                </div>
              </div>
            );
          }

          return (
            <Link
              data-testid={`module-card-${m.code}`}
              data-locked="0"
              key={m.code}
              to={url}
              className="group rounded-2xl bg-white border border-[#E7E0D2] p-5 hover:shadow-[0_10px_30px_-15px_rgba(6,78,59,0.2)] hover:border-[#064E3B] transition relative">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl grid place-items-center font-mono text-sm font-bold text-white" style={{ background: m.color }}>
                  {String(m.n).padStart(2, "0")}
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Green / Red status light per SRS visual spec */}
                  {mp.passed ? (
                    <span data-testid={`light-pass-${m.code}`}
                      title={t("modules.light_pass_title")}
                      className="inline-block w-2.5 h-2.5 rounded-full bg-[#10B981] shadow-[0_0_0_3px_rgba(16,185,129,0.18)]" />
                  ) : (mp.attempts || 0) > 0 ? (
                    <span data-testid={`light-fail-${m.code}`}
                      title={t("modules.light_fail_title")}
                      className="inline-block w-2.5 h-2.5 rounded-full bg-[#EF4444] shadow-[0_0_0_3px_rgba(239,68,68,0.18)]" />
                  ) : null}
                  {mp.passed && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981] text-white">{t("modules.passed")}</span>}
                  {tab === "practice" && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#D97706] bg-[#FFF7ED] border border-[#FED7AA] rounded-full px-2 py-0.5">
                      <Mic className="w-3 h-3" /> {t("dashboard.tabs.practice")}
                    </span>
                  )}
                </div>
              </div>
              <div className="font-heading mt-4 text-base font-medium text-[#1F2937]">{resolveModuleName(t, m)}</div>
              <div className={resolveModuleSubtitle(t, m) === m.name_ar ? "font-arabic text-sm text-[#4B5563]" : "text-sm text-[#4B5563]"}>{resolveModuleSubtitle(t, m)}</div>
              {m.target_seconds > 0 && (
                <div className="mt-3 text-xs text-[#4B5563]">{t("modules.target_attempts", { s: m.target_seconds, n: mp.attempts || 0 })}</div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Three-layer assessment cards (SRS v2.0 Section 10): Self / Peer /
 * Official. Each layer is independent and clearly labelled with both
 * status and counters. Peer review never blocks the certification path.
 */
function ThreeLayerCards({ t, modulesPassed, modulesTotal, peerModulesComplete, peerModulesTotal, certificateStatus, finalScore }) {
  const certBadge = certificateStatus === "issued"
    ? { label: t("dashboard.cert_issued"), tone: "bg-[#10B981] text-white" }
    : certificateStatus === "in_progress"
      ? { label: t("dashboard.cert_in_progress"), tone: "bg-[#FFF7ED] text-[#B45309] border border-[#FED7AA]" }
      : { label: t("dashboard.cert_not_submitted"), tone: "bg-[#F5EFE3] text-[#4B5563] border border-[#E7E0D2]" };
  return (
    <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="three-layer-cards">
      {/* Layer 1 — Self Assessment — whole card is clickable, mirrors Peer */}
      <Link to="/certificate?tab=self" data-testid="layer-self" className="rounded-2xl bg-white border border-[#E7E0D2] p-5 hover:border-[#064E3B] transition">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-[#064E3B] font-semibold">{t("dashboard.layer_self_title")}</div>
          <BookOpen className="w-5 h-5 text-[#064E3B]" />
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <div data-testid="layer-self-count" className="font-heading text-3xl font-semibold text-[#1F2937] tabular-nums">{modulesPassed}/{modulesTotal}</div>
        </div>
        <div className="text-xs text-[#4B5563] mt-1">{t("dashboard.layer_self_sub")}</div>
        <div className="mt-3 h-1.5 bg-[#E7E0D2] rounded-full overflow-hidden">
          <div className="h-full bg-[#064E3B] transition-all" style={{ width: `${Math.round(100 * modulesPassed / Math.max(modulesTotal, 1))}%` }} />
        </div>
        <span data-testid="layer-self-cta" className="mt-3 inline-block text-xs text-[#064E3B] hover:underline">
          {t("dashboard.layer_self_cta")} →
        </span>
      </Link>

      {/* Layer 2 — Peer Assessment — module-review progress (mirrors Self
          Assessment's shape), not the "reviews I've given others" stat,
          which already lives on the Community Peer Review tab. */}
      <Link to="/certificate?tab=peer" data-testid="layer-peer" className="rounded-2xl bg-white border border-[#E7E0D2] p-5 hover:border-[#D97706] transition">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-[#D97706] font-semibold">{t("dashboard.layer_peer_title")}</div>
          <Users className="w-5 h-5 text-[#D97706]" />
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <div data-testid="layer-peer-count" className="font-heading text-3xl font-semibold text-[#1F2937] tabular-nums">{peerModulesComplete}/{peerModulesTotal}</div>
        </div>
        <div className="text-xs text-[#4B5563] mt-1">{t("dashboard.layer_peer_sub")}</div>
        <div className="mt-3 h-1.5 bg-[#E7E0D2] rounded-full overflow-hidden">
          <div className="h-full bg-[#D97706] transition-all" style={{ width: `${Math.round(100 * peerModulesComplete / Math.max(peerModulesTotal, 1))}%` }} />
        </div>
        <span data-testid="layer-peer-cta" className="mt-3 inline-block text-xs text-[#D97706] hover:underline">
          {t("dashboard.layer_peer_cta")} →
        </span>
      </Link>

      {/* Layer 3 — Official Certification */}
      <Link to="/certificate?tab=official" data-testid="layer-cert" className="rounded-2xl bg-white border border-[#E7E0D2] p-5 hover:border-[#7C3AED] transition">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-[#7C3AED] font-semibold">{t("dashboard.layer_cert_title")}</div>
          <ShieldCheck className="w-5 h-5 text-[#7C3AED]" />
        </div>
        <div className="mt-3">
          <span data-testid="layer-cert-status" className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${certBadge.tone}`}>{certBadge.label}</span>
        </div>
        {finalScore > 0 && (
          <div className="text-xs text-[#4B5563] mt-2">{t("dashboard.layer_cert_score", { s: finalScore })}</div>
        )}
        <span data-testid="layer-cert-cta" className="mt-3 inline-block text-xs text-[#7C3AED] hover:underline">
          {t("dashboard.layer_cert_cta")} →
        </span>
      </Link>
    </div>
  );
}

function greeting(t) {
  const h = new Date().getHours();
  if (h < 5) return t("dashboard.greeting_night");
  if (h < 12) return t("dashboard.greeting_morning");
  if (h < 18) return t("dashboard.greeting_afternoon");
  return t("dashboard.greeting_evening");
}

function AchievementsTile({ achievements = [], t }) {
  const earnedChips = achievements.filter((a) => a.earned && (a.id === "all_modules" || a.id === "peer_reviewed_all_modules"));
  return (
    <div className="mt-6">
      <Link
        to="/achievements"
        data-testid="achievements-tile"
        className="flex items-center justify-between rounded-3xl bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]/60 border border-[#FED7AA] p-5 sm:p-6 hover:border-[#D97706] transition group"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#D97706] text-white grid place-items-center shrink-0">
            <Trophy className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#92400E] font-semibold">{t("dashboard.achievements_eyebrow")}</div>
            <div className="font-heading text-lg text-[#1F2937] mt-0.5">{t("dashboard.achievements_title")}</div>
            <div className="text-xs text-[#4B5563]">{t("dashboard.achievements_subtitle")}</div>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-[#D97706] group-hover:translate-x-1 transition-transform" />
      </Link>
      {earnedChips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="achievements-earned-chips">
          {earnedChips.map((a) => (
            <Link key={a.id} to="/achievements" data-testid={`achievement-chip-${a.id}`}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-[#FED7AA] hover:border-[#D97706] text-xs font-medium text-[#92400E] transition">
              <Award className="w-3.5 h-3.5 text-[#D97706]" /> {a.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Alert when a Companion/Circle-mate has a new recording waiting on this
 * user's review — only rendered when there's actually something pending.
 */
function PendingReviewsAlert({ peer }) {
  const { t } = useI18n();
  const n = peer?.pending_for_me || 0;
  if (!n) return null;
  return (
    <Link
      to="/community?tab=peer"
      data-testid="pending-reviews-alert"
      className="mt-6 flex items-center justify-between rounded-2xl bg-[#FFF7ED] border border-[#FED7AA] px-5 py-4 hover:border-[#D97706] transition group">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#D97706] text-white grid place-items-center shrink-0">
          <Bell className="w-5 h-5" />
        </div>
        <div>
          <div className="font-medium text-[#1F2937]">{t("dashboard.pending_reviews", { count: n })}</div>
          <div className="text-xs text-[#4B5563]">{t("dashboard.pending_reviews_from")}</div>
        </div>
      </div>
      <ArrowRight className="w-5 h-5 text-[#D97706] group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

